/**
 * Canonical telemetry store reducer (spec `vehicle-realtime-data-consistency`,
 * design "Merge Rules" / step 5 part 1).
 *
 * State shape (design "Canonical Data Model"):
 *
 *   {
 *     currentByPlate: {
 *       [plateNumber]: {
 *         plateNumber, sourceId, latestPacketAt, latestPacketType,
 *         movement:  { speed, direction, observedAt, packetId, source, serverTime },
 *         ignition:  { value, observedAt, source, serverTime },
 *         position:  { value, observedAt, source, serverTime },
 *       },
 *     },
 *     tableByPlate: { [plateNumber]: [normalizedPacket, ...] }, // newest first
 *   }
 *
 * Current fields are stored independently because the latest-vehicles endpoint
 * aggregates them from different packets. Each field is replaced only by a newer
 * observation *of that field*, so generic packet activity (`latestPacketAt`) can
 * never refresh `movement.observedAt` (#119052) and the newest speed observation
 * across all three sources always wins (#119201).
 *
 * `serverTime` is carried on a field observation as tie-break metadata only.
 */
import {
  LATEST_VEHICLES_SOURCE,
  REALTIME_SOURCE,
  ROUTE_SOURCE,
  getPacketDedupKey,
  normalizeLatestVehicle,
  normalizePacket,
} from "./normalizers";

/** Maximum rendered rows per plate (design "Selected vehicle table" rule 6). */
export const MAX_TABLE_ROWS = 200;

/**
 * Deterministic source priority, used only as the final tie-breaker when two
 * observations share both `observedAt` and `serverTime`. A real packet is more
 * specific than the latest-vehicles per-field aggregate, and a live SignalR
 * packet is the most direct observation of the three.
 */
const SOURCE_PRIORITY = {
  [REALTIME_SOURCE]: 3,
  [ROUTE_SOURCE]: 2,
  [LATEST_VEHICLES_SOURCE]: 1,
};

export const initialState = { currentByPlate: {}, tableByPlate: {} };

const isValidPlate = (plateNumber) =>
  plateNumber !== null && plateNumber !== undefined && plateNumber !== "";

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

/** Observation time of a field, or `null` when its freshness is unknown. */
const readObservedAt = (field) => toFiniteNumber(field?.observedAt);

const sourcePriority = (source) => SOURCE_PRIORITY[source] ?? 0;

/**
 * Design comparator: an incoming observation replaces the current one only when
 * it is strictly newer. Unknown incoming freshness never wins; unknown current
 * freshness always loses to a timestamped observation.
 */
export const isNewerObservation = (incoming, current) => {
  const incomingAt = readObservedAt(incoming);
  if (incomingAt === null) return false;

  const currentAt = readObservedAt(current);
  if (currentAt === null) return true;

  return incomingAt > currentAt;
};

/**
 * Equal-timestamp tie-break (design merge rule 4): same packet id -> same
 * observation (keep the current one), otherwise the newer `serverTime` wins, and
 * finally the deterministic source priority decides.
 * @returns {boolean} `true` when the incoming observation should replace the current one.
 */
const winsEqualTimestampTieBreak = (incoming, current) => {
  const incomingPacketId = incoming?.packetId ?? null;
  const currentPacketId = current?.packetId ?? null;
  if (incomingPacketId !== null && incomingPacketId === currentPacketId) return false;

  const incomingServerTime = toFiniteNumber(incoming?.serverTime);
  const currentServerTime = toFiniteNumber(current?.serverTime);
  if (incomingServerTime !== currentServerTime) {
    if (incomingServerTime === null) return false;
    if (currentServerTime === null) return true;
    return incomingServerTime > currentServerTime;
  }

  return sourcePriority(incoming?.source) > sourcePriority(current?.source);
};

/**
 * Merges one field observation (movement / ignition / position).
 *
 * - An absent incoming observation never clears the current one, so a packet
 *   without speed cannot replace a valid movement observation (rule 6).
 * - An untimestamped observation is retained only when nothing is stored yet
 *   (unknown-freshness fallback); it never replaces a timestamped observation.
 *
 * @returns {Object|null} The winning observation (current one by reference when unchanged).
 */
export const mergeFieldObservation = (currentField, incomingField) => {
  if (!incomingField) return currentField ?? null;
  if (!currentField) return incomingField;

  if (isNewerObservation(incomingField, currentField)) return incomingField;

  const incomingAt = readObservedAt(incomingField);
  const currentAt = readObservedAt(currentField);
  if (incomingAt !== null && incomingAt === currentAt) {
    return winsEqualTimestampTieBreak(incomingField, currentField) ? incomingField : currentField;
  }

  return currentField;
};

/** Attaches tie-break metadata to a field observation coming from a packet. */
const withTieBreakMetadata = (field, { serverTime = null, packetId = null } = {}) => {
  if (!field) return null;
  return {
    ...field,
    packetId: field.packetId ?? packetId,
    serverTime: field.serverTime ?? toFiniteNumber(serverTime),
  };
};

const emptyEntry = (plateNumber) => ({
  plateNumber,
  sourceId: null,
  latestPacketAt: null,
  latestPacketType: null,
  movement: null,
  ignition: null,
  position: null,
});

/**
 * Merges one canonical observation set into the entry of a plate.
 *
 * @param {Object|null} entry - Current entry for the plate.
 * @param {Object} observation - `{ plateNumber, sourceId, latestPacketAt, latestPacketType, movement, ignition, position }`.
 * @returns {Object} The merged entry (same reference when nothing changed).
 */
const mergeEntry = (entry, observation) => {
  const current = entry ?? emptyEntry(observation.plateNumber);

  const movement = mergeFieldObservation(current.movement, observation.movement);
  const ignition = mergeFieldObservation(current.ignition, observation.ignition);
  const position = mergeFieldObservation(current.position, observation.position);

  // Generic activity only. Never feeds movement.observedAt (design rule 5).
  const incomingPacketAt = toFiniteNumber(observation.latestPacketAt);
  const currentPacketAt = toFiniteNumber(current.latestPacketAt);
  const packetAtAdvances =
    incomingPacketAt !== null && (currentPacketAt === null || incomingPacketAt > currentPacketAt);
  const latestPacketAt = packetAtAdvances ? incomingPacketAt : currentPacketAt;
  const latestPacketType = packetAtAdvances
    ? (observation.latestPacketType ?? null)
    : (current.latestPacketType ?? null);
  const sourceId = current.sourceId ?? observation.sourceId ?? null;

  const unchanged =
    entry &&
    movement === current.movement &&
    ignition === current.ignition &&
    position === current.position &&
    latestPacketAt === current.latestPacketAt &&
    latestPacketType === current.latestPacketType &&
    sourceId === current.sourceId;

  if (unchanged) return entry;

  return {
    ...current,
    plateNumber: current.plateNumber ?? observation.plateNumber,
    sourceId,
    latestPacketAt,
    latestPacketType,
    movement,
    ignition,
    position,
  };
};

/** Observation set contributed by a normalized route/SignalR packet. */
const observationFromPacket = (packet) => ({
  plateNumber: packet.plateNumber,
  sourceId: packet.sourceId ?? null,
  latestPacketAt: packet.latestPacketAt,
  latestPacketType: packet.packetType ?? null,
  movement: withTieBreakMetadata(packet.fields?.movement, packet),
  ignition: withTieBreakMetadata(packet.fields?.ignition, packet),
  position: withTieBreakMetadata(packet.fields?.position, packet),
});

const applyObservations = (currentByPlate, observations) => {
  let next = currentByPlate;
  let changed = false;

  observations.forEach((observation) => {
    if (!observation || !isValidPlate(observation.plateNumber)) return;

    const plate = observation.plateNumber;
    const merged = mergeEntry(next[plate], observation);
    if (merged === next[plate]) return;

    if (!changed) {
      next = { ...currentByPlate };
      changed = true;
    }
    next[plate] = merged;
  });

  return changed ? next : currentByPlate;
};

const compareSourceTimeDesc = (a, b) => {
  const aTime = toFiniteNumber(a?.sourceTime);
  const bTime = toFiniteNumber(b?.sourceTime);
  if (aTime === bTime) return 0;
  // Rows with an unknown time sort last so the newest known packet stays first.
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  return bTime - aTime;
};

/** Newest-first ordering for the live table (design table rule 4). */
export const sortRowsNewestFirst = (rows = []) => [...rows].sort(compareSourceTimeDesc);

/** Oldest-first ordering, derived separately for the map polyline (rule 5). */
export const sortRowsOldestFirst = (rows = []) =>
  [...rows].sort((a, b) => {
    const aTime = toFiniteNumber(a?.sourceTime);
    const bTime = toFiniteNumber(b?.sourceTime);
    if (aTime === bTime) return 0;
    // Unknown times stay last in both orderings.
    if (aTime === null) return 1;
    if (bTime === null) return -1;
    return aTime - bTime;
  });

/**
 * Adds real packets to a plate's table: dedup by `packetId` (fallback
 * `plateNumber + sourceId + sourceTime + packetType`), newest first, capped at
 * `MAX_TABLE_ROWS`. The first occurrence of a duplicate is kept, matching the
 * existing `removeDuplicates` behavior (design 3.8).
 */
export const addPacketsToTable = (rows = [], packets = []) => {
  if (!packets.length) return rows;

  const seen = new Set(rows.map(getPacketDedupKey));
  const added = [];

  packets.forEach((packet) => {
    const key = getPacketDedupKey(packet);
    if (seen.has(key)) return;
    seen.add(key);
    added.push(packet);
  });

  if (!added.length) return rows;

  return sortRowsNewestFirst([...rows, ...added]).slice(0, MAX_TABLE_ROWS);
};

const applyTableRows = (tableByPlate, rowsByPlate) => {
  let next = tableByPlate;
  let changed = false;

  Object.entries(rowsByPlate).forEach(([plate, packets]) => {
    const rows = addPacketsToTable(tableByPlate[plate] ?? [], packets);
    if (rows === tableByPlate[plate]) return;

    if (!changed) {
      next = { ...tableByPlate };
      changed = true;
    }
    next[plate] = rows;
  });

  return changed ? next : tableByPlate;
};

const groupByPlate = (packets) =>
  packets.reduce((groups, packet) => {
    const plate = packet.plateNumber;
    (groups[plate] = groups[plate] ?? []).push(packet);
    return groups;
  }, {});

const normalizePackets = (payload, source, plateNumber) => {
  const packets = Array.isArray(payload) ? payload : [payload];

  return packets
    .map((packet) => normalizePacket(packet, source))
    .filter(
      (packet) =>
        packet &&
        isValidPlate(packet.plateNumber) &&
        // Route responses are per plate; a foreign row is ignored so the selected
        // vehicle's table can never show another vehicle's packets.
        (!isValidPlate(plateNumber) || packet.plateNumber === plateNumber),
    );
};

/**
 * `HYDRATE_LATEST_VEHICLES`: merges the per-field aggregate of
 * `GET /formatted/latest/vehicles`. Never writes table rows - an aggregate row
 * would mix fields observed at different times (design 3.5).
 */
export const mergeLatestFieldAggregates = (state, payload) => {
  const items = Array.isArray(payload) ? payload : [payload];
  const observations = items.map(normalizeLatestVehicle).filter(Boolean);
  const currentByPlate = applyObservations(state.currentByPlate, observations);

  return currentByPlate === state.currentByPlate ? state : { ...state, currentByPlate };
};

/**
 * `HYDRATE_ROUTE`: merges chronological route packets for one plate into both
 * the table and the current field state (design merge rule 7).
 */
export const mergeRoutePackets = (state, plateNumber, payload) => {
  const packets = normalizePackets(payload, ROUTE_SOURCE, plateNumber);
  if (!packets.length) return state;

  const currentByPlate = applyObservations(
    state.currentByPlate,
    packets.map(observationFromPacket),
  );
  const tableByPlate = applyTableRows(state.tableByPlate, groupByPlate(packets));

  return currentByPlate === state.currentByPlate && tableByPlate === state.tableByPlate
    ? state
    : { ...state, currentByPlate, tableByPlate };
};

/**
 * `MERGE_REALTIME`: merges live SignalR packets. Packets are kept per plate, so
 * an update for another vehicle never evicts the selected vehicle's data
 * (design 2.5). A non-speed packet updates `latestPacketAt` only.
 */
export const mergeRealtimePackets = (state, payload) => {
  const packets = normalizePackets(payload, REALTIME_SOURCE);
  if (!packets.length) return state;

  const currentByPlate = applyObservations(
    state.currentByPlate,
    packets.map(observationFromPacket),
  );
  const tableByPlate = applyTableRows(state.tableByPlate, groupByPlate(packets));

  return currentByPlate === state.currentByPlate && tableByPlate === state.tableByPlate
    ? state
    : { ...state, currentByPlate, tableByPlate };
};

/**
 * `CLEAR_TABLE`: drops table rows so a selection change cannot show the previous
 * vehicle's packets. With a plate number, only that plate's rows are cleared;
 * without one, every plate's rows are cleared. Current field state is kept -
 * the sidebar still needs each vehicle's canonical movement.
 */
export const clearSelectedTable = (state, plateNumber) => {
  if (!isValidPlate(plateNumber)) {
    return Object.keys(state.tableByPlate).length === 0 ? state : { ...state, tableByPlate: {} };
  }

  if (!(plateNumber in state.tableByPlate)) return state;

  const tableByPlate = { ...state.tableByPlate };
  delete tableByPlate[plateNumber];

  return { ...state, tableByPlate };
};

/**
 * Canonical telemetry reducer. All branches share the same comparator and dedup
 * rules so no source can win by response order.
 */
export function telemetryReducer(state = initialState, action = {}) {
  switch (action?.type) {
    case "HYDRATE_LATEST_VEHICLES":
      return mergeLatestFieldAggregates(state, action.payload);
    case "HYDRATE_ROUTE":
      return mergeRoutePackets(state, action.plateNumber, action.payload);
    case "MERGE_REALTIME":
      return mergeRealtimePackets(state, action.payload);
    case "CLEAR_TABLE":
      return clearSelectedTable(state, action.plateNumber);
    default:
      return state;
  }
}
